import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { Condo, Unit, Owner, Project } from '../../types';

export const createUtilitySlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set) => ({
  // Utility Methods
  initializeSampleData: (condoId?: string) => {
    const sampleCondos: Condo[] = [
      {
        id: '1',
        name: 'Ocean View Towers',
        address: '123 Beachfront Ave',
        city: 'Miami',
        state: 'FL',
        zipCode: '33139',
        totalUnits: 50,
        managedSince: '2020-01-01',
        contactEmail: 'manager@oceanview.com',
        contactPhone: '+1 (555) 123-4567',
        monthlyFee: 350,
        isActive: true,
        createdAt: '2020-01-01',
        updatedAt: '2024-01-01',
      },
      {
        id: '2',
        name: 'Mountain Peak Residences',
        address: '456 Highland Rd',
        city: 'Denver',
        state: 'CO',
        zipCode: '80202',
        totalUnits: 30,
        managedSince: '2021-03-15',
        contactEmail: 'admin@mountainpeak.com',
        contactPhone: '+1 (555) 987-6543',
        monthlyFee: 420,
        isActive: true,
        createdAt: '2021-03-15',
        updatedAt: '2024-01-01',
      },
    ];

    const sampleUnits: Unit[] = [
      // Ocean View Towers Units
      {
        id: 'unit-1',
        condoId: '1',
        unitNumber: '101',
        floor: 1,
        area: 850,
        bedrooms: 1,
        bathrooms: 1,
        status: 'occupied',
        ownerId: 'owner-1',
        features: ['Balcony', 'Garden View']
      },
      {
        id: 'unit-2',
        condoId: '1',
        unitNumber: '102',
        floor: 1,
        area: 900,
        bedrooms: 1,
        bathrooms: 1,
        status: 'available',
        features: ['Balcony', 'Pool View']
      },
      {
        id: 'unit-3',
        condoId: '1',
        unitNumber: '205',
        floor: 2,
        area: 1200,
        bedrooms: 2,
        bathrooms: 2,
        status: 'occupied',
        ownerId: 'owner-2',
        features: ['Balcony', 'Ocean View', 'Walk-in Closet']
      },
      {
        id: 'unit-4',
        condoId: '1',
        unitNumber: '206',
        floor: 2,
        area: 1250,
        bedrooms: 2,
        bathrooms: 2,
        status: 'maintenance',
        features: ['Balcony', 'Ocean View']
      },
      {
        id: 'unit-5',
        condoId: '1',
        unitNumber: '301',
        floor: 3,
        area: 1500,
        bedrooms: 3,
        bathrooms: 2,
        status: 'occupied',
        ownerId: 'owner-5',
        features: ['Balcony', 'Ocean View', 'Walk-in Closet', 'Jacuzzi']
      },
      // Mountain Peak Residences Units
      {
        id: 'unit-6',
        condoId: '2',
        unitNumber: '101',
        floor: 1,
        area: 800,
        bedrooms: 1,
        bathrooms: 1,
        status: 'occupied',
        ownerId: 'owner-3',
        features: ['Mountain View', 'Fireplace']
      },
      {
        id: 'unit-7',
        condoId: '2',
        unitNumber: '102',
        floor: 1,
        area: 850,
        bedrooms: 1,
        bathrooms: 1,
        status: 'available',
        features: ['Garden View']
      },
      {
        id: 'unit-8',
        condoId: '2',
        unitNumber: '301',
        floor: 3,
        area: 1300,
        bedrooms: 2,
        bathrooms: 2,
        status: 'occupied',
        ownerId: 'owner-8',
        features: ['Mountain View', 'Fireplace', 'Balcony']
      },
      {
        id: 'unit-9',
        condoId: '2',
        unitNumber: '302',
        floor: 3,
        area: 1350,
        bedrooms: 2,
        bathrooms: 2,
        status: 'occupied',
        ownerId: 'owner-9',
        features: ['Mountain View', 'Balcony']
      },
      {
        id: 'unit-10',
        condoId: '2',
        unitNumber: '401',
        floor: 4,
        area: 1800,
        bedrooms: 3,
        bathrooms: 3,
        status: 'available',
        features: ['Mountain View', 'Balcony', 'Walk-in Closet', 'Jacuzzi']
      },
    ];

    const sampleOwners: Owner[] = [
      {
        id: 'owner-1',
        condoId: '1',
        name: 'John Smith',
        email: 'john.smith@example.com',
        phone: '(555) 123-4567',
        unitNumber: '101',
        moveInDate: new Date('2023-01-15'),
        status: 'active'
      },
      {
        id: 'owner-2',
        condoId: '1',
        name: 'Maria Garcia',
        email: 'maria.garcia@example.com',
        phone: '(555) 234-5678',
        unitNumber: '205',
        moveInDate: new Date('2023-02-20'),
        status: 'active'
      },
      {
        id: 'owner-3',
        condoId: '2',
        name: 'Robert Johnson',
        email: 'robert.johnson@example.com',
        phone: '(555) 345-6789',
        unitNumber: '101',
        moveInDate: new Date('2023-03-10'),
        status: 'active'
      },
      {
        id: 'owner-5',
        condoId: '1',
        name: 'Sarah Williams',
        email: 'sarah.williams@example.com',
        phone: '(555) 456-7890',
        unitNumber: '301',
        moveInDate: new Date('2023-04-05'),
        status: 'active'
      },
      {
        id: 'owner-8',
        condoId: '2',
        name: 'Michael Brown',
        email: 'michael.brown@example.com',
        phone: '(555) 567-8901',
        unitNumber: '301',
        moveInDate: new Date('2023-05-12'),
        status: 'active'
      },
      {
        id: 'owner-9',
        condoId: '2',
        name: 'Jennifer Davis',
        email: 'jennifer.davis@example.com',
        phone: '(555) 678-9012',
        unitNumber: '302',
        moveInDate: new Date('2023-06-18'),
        status: 'active'
      },
    ];

    const sampleProjects: Project[] = [
      {
        id: 'project-1',
        condoId: '1',
        name: 'Lobby Renovation',
        description: 'Complete renovation of the main lobby area including new flooring, lighting, and furniture.',
        budget: 50000,
        actualCost: 48500,
        responsiblePerson: 'John Smith',
        responsiblePersonId: 'owner-1',
        startDate: new Date('2024-01-15'),
        endDate: new Date('2024-03-30'),
        status: 'COMPLETED',
        priority: 'HIGH',
        category: 'renovation',
        notes: 'Project completed on time and under budget',
        createdAt: new Date('2024-01-10'),
        updatedAt: new Date('2024-03-30')
      },
      {
        id: 'project-2',
        condoId: '1',
        name: 'Pool Deck Resurfacing',
        description: 'Resurface the pool deck and install new safety features.',
        budget: 35000,
        actualCost: 28000,
        responsiblePerson: 'Maria Garcia',
        responsiblePersonId: 'owner-2',
        startDate: new Date('2024-02-01'),
        endDate: new Date('2024-04-15'),
        status: 'IN_PROGRESS',
        priority: 'MEDIUM',
        category: 'maintenance',
        notes: 'Work is progressing well, expected to be completed by mid-April',
        createdAt: new Date('2024-01-25'),
        updatedAt: new Date('2024-03-15')
      },
      {
        id: 'project-3',
        condoId: '1',
        name: 'Security System Upgrade',
        description: 'Install new security cameras and access control system throughout the building.',
        budget: 75000,
        actualCost: 72000,
        responsiblePerson: 'Sarah Williams',
        responsiblePersonId: 'owner-5',
        startDate: new Date('2024-03-01'),
        endDate: new Date('2024-05-30'),
        status: 'PLANNING',
        priority: 'URGENT',
        category: 'security',
        notes: 'Awaiting approval from board',
        createdAt: new Date('2024-02-15'),
        updatedAt: new Date('2024-02-15')
      },
      {
        id: 'project-4',
        condoId: '2',
        name: 'Roof Repair',
        description: 'Repair leaks and replace damaged roofing materials.',
        budget: 25000,
        actualCost: 23000,
        responsiblePerson: 'Robert Johnson',
        responsiblePersonId: 'owner-3',
        startDate: new Date('2024-01-10'),
        endDate: new Date('2024-02-28'),
        status: 'COMPLETED',
        priority: 'HIGH',
        category: 'maintenance',
        notes: 'All repairs completed, final inspection passed',
        createdAt: new Date('2024-01-05'),
        updatedAt: new Date('2024-02-28')
      },
      {
        id: 'project-5',
        condoId: '2',
        name: 'Landscaping Enhancement',
        description: 'Redesign and enhance the common area landscaping.',
        budget: 15000,
        actualCost: 12000,
        responsiblePerson: 'Michael Brown',
        responsiblePersonId: 'owner-8',
        startDate: new Date('2024-04-01'),
        endDate: new Date('2024-06-15'),
        status: 'PLANNING',
        priority: 'LOW',
        category: 'landscaping',
        notes: 'Awaiting spring weather to begin work',
        createdAt: new Date('2024-03-01'),
        updatedAt: new Date('2024-03-01')
      },
      {
        id: 'project-6',
        condoId: '2',
        name: 'Parking Lot Repaving',
        description: 'Repave and restripe the parking lot.',
        budget: 45000,
        actualCost: 0,
        responsiblePerson: 'Jennifer Davis',
        responsiblePersonId: 'owner-9',
        startDate: new Date('2024-05-01'),
        endDate: new Date('2024-07-31'),
        status: 'ON_HOLD',
        priority: 'MEDIUM',
        category: 'infrastructure',
        notes: 'On hold due to budget constraints',
        createdAt: new Date('2024-02-20'),
        updatedAt: new Date('2024-03-10')
      }
    ];

    // Filter sample data by condoId if specified
    const filteredUnits = condoId ?
      sampleUnits.filter(unit => unit.condoId === condoId) :
      sampleUnits;

    const filteredOwners = condoId ?
      sampleOwners.filter(owner => owner.condoId === condoId) :
      sampleOwners;

    const filteredProjects = condoId ?
      sampleProjects.filter(project => project.condoId === condoId) :
      sampleProjects;

    set({
      condos: sampleCondos,
      units: filteredUnits,
      owners: filteredOwners,
      projects: filteredProjects,
      fees: [],
      expenses: [],
      revenues: [],
      documents: [],
      statements: [],
      payments: []
    });
  },

  clearAllData: () => {
    set({
      condos: [],
      units: [],
      owners: [],
      fees: [],
      expenses: [],
      revenues: [],
      statements: [],
      payments: [],
      documents: [],
      projects: []
    });
  },
});
